import React, { useState } from "react";
import { CloseButton, Form, Table } from "react-bootstrap";
import { useFormContext } from "react-hook-form";

import useFileTypeValidator from "../hooks/useFileValidator";
import { IFileUploadPropsWithValidation } from "../types/forms";

const FileUpload = ({
    maxFileCount = 5,
    allowedFileExtensions,
    maxFileSize,
    fileList,
    setFileList,
}: IFileUploadPropsWithValidation) => {
    const { register, setError, clearErrors } = useFormContext();
    const [inputKey, setInputKey] = useState<string>("");
    const { validateFileType, validateFileSize } = useFileTypeValidator({
        maxFileSize,
        allowedFileExtensions,
    });

    const resetFileInput = (): void => {
        const randomString = Math.random().toString(36);
        setInputKey(randomString);
    };

    const handleDelete = (fileName: string) => {
        setFileList(prevlist =>
            prevlist.filter(item => item.name !== fileName),
        );
    };

    const formatBytes = (bytes: number): string => {
        const sizes = ["Bytes", "KB", "MB", "GB", "TB"];

        if (bytes === 0) return "0 Bytes";

        const i = Math.floor(Math.log(bytes) / Math.log(1024));

        return (
            Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + " " + sizes[i]
        );
    };

    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();

        if (e.currentTarget.files) {
            const files: FileList = e.currentTarget.files as FileList;
            const isValidType = validateFileType(files[0]);
            const isValidSize = validateFileSize(files[0]);

            if (fileList.some((file: File) => file.name === files[0].name)) {
                setError("files", {
                    type: "custom",
                    message: `File ${files[0].name} already queued for upload\n`,
                });
                resetFileInput();
                return;
            }

            if (isValidType && isValidSize) {
                setFileList(prev => [...prev, files[0]]);
                clearErrors("files");
            } else {
                let errorMessage = "";
                errorMessage += !isValidType
                    ? `File type (${files[0].type.split("/").pop()}) is invalid.
                          Allowable types include: ${allowedFileExtensions.join(
                              ", ",
                          )}\n`
                    : "";

                errorMessage += !isValidSize
                    ? `File size is too big:
                    File size: ${formatBytes(files[0].size)}
                    Max: ${formatBytes(maxFileSize)}`
                    : "";

                setError("files", {
                    type: "custom",
                    message: errorMessage,
                });
            }

            resetFileInput();
        } else {
            setError("files", {
                type: "custom",
                message: "Please upload at least 1 file",
            });
        }
    };

    const getTableBodyAsReactElement = () => (
        <Table bordered striped hover>
            <thead>
                <tr>
                    <th scope="col">#</th>
                    <th scope="col">Name</th>
                    <th scope="col">Size</th>
                    <th scope="col">Type</th>
                    <th scope="col"></th>
                </tr>
            </thead>
            <tbody>
                {fileList.map((item, i) => {
                    return (
                        <tr key={i}>
                            <td key={i + ":#"}>{i + 1}</td>
                            <td>{item.name}</td>
                            <td>
                                {item.size} ({formatBytes(item.size)})
                            </td>
                            <td>{item.type}</td>
                            <td>
                                <CloseButton
                                    key={i + ":del"}
                                    onClick={e => handleDelete(item.name)}
                                ></CloseButton>
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </Table>
    );

    const renderFileInput = () => {
        if (!(maxFileCount > 0) || fileList.length < maxFileCount) {
            return (
                <Form.Control
                    {...register("files")}
                    type="file"
                    onChange={handleOnChange}
                    key={inputKey}
                    data-testid="file-input"
                />
            );
        }
    };

    return (
        <>
            {fileList.length !== 0 && getTableBodyAsReactElement()}
            {renderFileInput()}
        </>
    );
};

export default FileUpload;
